import FeedIcon from '@mui/icons-material/Feed';
import { Button, Chip } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { BenefitDTOBenefitEnum, BikeleasingContractListItemDTO } from 'probonio-shared-ui/api';
import { getBikeleasingContractStatusColor } from 'probonio-shared-ui/utils/getBikeleasingContractStatusColor';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigateWithPrevPath } from '../../../util/useNavigateWithPrevPath';

export function useBikeleasingContractColumns(): MRT_ColumnDef<BikeleasingContractListItemDTO>[] {
  const { t } = useTranslation('benefitsModule');
  const navigate = useNavigateWithPrevPath();

  const handleGoToContract = useCallback(
    (contractId: string) => (event: React.MouseEvent) => {
      event.preventDefault();
      navigate(`/benefits/${BenefitDTOBenefitEnum.BikeleasingV2.toLowerCase()}/contracts/${contractId}`, {
        state: { forceHistoryBack: true },
      });
    },
    [navigate],
  );

  return useMemo<MRT_ColumnDef<BikeleasingContractListItemDTO>[]>(
    () => [
      {
        accessorFn: row => t(`extensions:bikeleasing_v2_benefit.contract.status.${row.status ?? 'UNKNOWN'}`),
        enableSorting: false,
        header: t('extensions:bikeleasing_v2_benefit.contractsTable.header.status'),
        Cell: ({ row }) => {
          return (
            <Chip
              variant="filled"
              label={t(`extensions:bikeleasing_v2_benefit.contract.status.${row.original.status ?? 'UNKNOWN'}`)}
              color={getBikeleasingContractStatusColor(row.original.status)}
              sx={{
                borderRadius: 1,
                height: theme => theme.spacing(3),
                fontSize: theme => theme.typography.h3.fontSize,
                fontWeight: '600',
                '& .MuiChip-label': {
                  px: 1.5,
                },
              }}
            />
          );
        },
      },

      {
        id: 'employee',
        header: t('extensions:bikeleasing_v2_benefit.contractsTable.header.name'),
        accessorKey: 'lastName', // optional, used for sorting
        sortBy: ['employee.lastName', 'employee.firstName'],
        Cell: ({ row }) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ marginLeft: 10 }}>
              <b>
                {row.original?.lastName}
                {row.original.firstName && row.original.lastName && ','} {row.original.firstName}
              </b>
              <div>{row.original.email}</div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'contractNumber',
        header: t('extensions:bikeleasing_v2_benefit.contractsTable.header.contractNumber'),
      },
      {
        accessorKey: 'startDate',
        header: t('extensions:bikeleasing_v2_benefit.contractsTable.header.startDate'),
        Cell: ({ cell }) => (cell.getValue<string>() ? t('common:date', { date: new Date(cell.getValue<string>()) }) : null),
      },
      {
        accessorKey: 'endDate',
        header: t('extensions:bikeleasing_v2_benefit.contractsTable.header.endDate'),
        Cell: ({ cell }) => (cell.getValue<string>() ? t('common:date', { date: new Date(cell.getValue<string>()) }) : null),
      },
      {
        id: 'contractLink',
        header: '',
        enableSorting: false,
        Cell: ({ row }) => {
          const contract = row.original;
          return contract.id ? (
            <Button variant="text" startIcon={<FeedIcon />} onClick={handleGoToContract(contract.id)}>
              {t('extensions:bikeleasing_v2_benefit.contractLabel')}
            </Button>
          ) : null;
        },
      },
    ],
    [t, handleGoToContract],
  );
}
